import React, { useMemo } from 'react';
import ConfirmModalLayout from '@shared/components/user-management/ConfirmModalLayout';
import BulkActionButton from '@shared/components/BulkActionButton';

const statusLabel = (status) => {
	if (status === 'paid') return 'Paid';
	if (status === 'unpaid' || status === 'pending') return 'Unpaid';
	return 'Not Qualified';
};

const normalizeStatus = (status) => (status === 'pending' ? 'unpaid' : status);

const RoyaltyPaymentActionModal = ({
	open,
	onClose,
	targetStatus, // 'paid' | 'unpaid'
	selectedRows = [],
	onConfirm,
	year,
	quarter,
}) => {
	const { eligibleRows, ineligibleRows } = useMemo(() => {
		const eligible = [];
		const ineligible = [];
		selectedRows.forEach((row) => {
			const current = normalizeStatus(row?.status);
			if (row?.qualified && current !== targetStatus) eligible.push(row);
			else ineligible.push(row);
		});
		return { eligibleRows: eligible, ineligibleRows: ineligible };
	}, [selectedRows, targetStatus]);

	const title = targetStatus === 'paid' ? 'Mark as Paid' : 'Mark as Unpaid';
	const confirmText = targetStatus === 'paid' ? 'Mark Paid' : 'Mark Unpaid';
	const confirmMessage =
		targetStatus === 'paid'
			? 'Are you sure you want to mark the selected schools as Paid?'
			: 'Are you sure you want to mark the selected schools as Unpaid?';

	const content = (
		<div className="flex flex-col gap-12 w-full">
			{ineligibleRows?.length ? (
				<div className="w-full p-12 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
					{ineligibleRows.length} item{ineligibleRows.length > 1 ? 's' : ''} will be skipped (already{' '}
					{targetStatus} or not qualified).
				</div>
			) : null}
			<span className="text-16 text-black">
				The following records will be marked as <span className="font-bold align-bottom capitalize">{targetStatus}</span>
			</span>
			<div className="flex flex-col gap-4 w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
				{selectedRows.map((row, index) => {
					const isLast = index === selectedRows.length - 1;
					const current = normalizeStatus(row?.status);
					const willChange = row?.qualified && current !== targetStatus;
					return (
						<div
							className={`flex items-center gap-8 p-16 ${!isLast ? 'border-b' : ''} ${
								willChange ? '' : 'opacity-60 bg-black/5'
							}`}
							key={row?.school?.id}
						>
							<div className="flex-1">
								<p className="font-medium">{row?.school?.name ?? row?.company?.name ?? '-'}</p>
								<p className="text-12 text-gray-600">Current: {statusLabel(row?.status)}</p>
								{year && quarter ? (
									<p className="text-12 text-gray-600">
										Year: {year}, Quarter: Q{quarter}
									</p>
								) : null}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);

	const footer = (
		<div className="flex flex-col gap-12 w-full md:flex-row items-center">
			<p className="text-gray-600 shrink-0">{confirmMessage}</p>
			<div className="flex gap-12 justify-end w-full">
				<BulkActionButton onClick={onClose} type="cancel">
					Cancel
				</BulkActionButton>
				<BulkActionButton disabled={eligibleRows.length === 0} onClick={onConfirm} type="button">
					{confirmText}
				</BulkActionButton>
			</div>
		</div>
	);

	return (
		<ConfirmModalLayout
			content={content}
			footer={footer}
			onClose={onClose}
			open={open}
			title={title}
			width={{ xs: '95vw', md: '80vw' }}
		/>
	);
};

export default RoyaltyPaymentActionModal;
